import { call, cancelled, put, takeLatest } from 'redux-saga/effects';
import {
  GET_AGENT_INFO_BY_CODE,
  GET_OPM_ASSIGNMENT_LIST,
  GET_AGENTS_UNDER_AD,
  GET_ELIGIBLE_POLICIES,
  GET_SUITABLE_AGENTS,
  APPROVE_OPM_ASSIGNMENT,
  REJECT_OPM_ASSIGNMENT,
} from './constants';
import {
  setAgentInfoByCodeAction,
  setOpmAssignmentListAction,
  setAgentsUnderAdAction,
  setEligiblePoliciesAction,
  setSuitableAgentsAction,
} from './actions';
import {
  getAgentInformationByAgentCodeService,
  getOpmAssignmentListService,
  getAllOpmAssignmentsService,
  getAgentsUnderAdService,
  getEligiblePoliciesService,
  getSuitableAgentsService,
  validateAgentForAssignmentService,
  resumeStepFunctionService,
} from '@services/opm';
import React from 'react';
import { loadingRequest, loadingSuccess } from '@stores/global/actions';
import { GlobalModal as _GlobalModal } from '@components/molecules/PopupModal/GlobalModal';
import { ErrorLevelConfig } from '@core/configureStore/ErrorLevelConfig';
import { store } from '@core/configureStore/store';

const GlobalModal = {
  openOnce: ({ message, ...rest }) =>
    _GlobalModal.openOnce({
      message: React.createElement(
        'span',
        { style: { fontSize: '1rem' } },
        message,
      ),
      ...rest,
    }),
};

export function* getAgentInfoByCodeSaga({ payload }) {
  try {
    yield put(loadingRequest());
    const response = yield call(getAgentInformationByAgentCodeService, payload);
    yield put(loadingSuccess());

    yield put(setAgentInfoByCodeAction(response?.data ?? response));
  } catch (err) {
    console.error(err);
    yield put(loadingSuccess());
    GlobalModal.openOnce({
      title: 'Error',
      message: ErrorLevelConfig.SAGA.FETCH_ERROR,
      okText: 'Đóng',
      onOk: () => {},
    });
  }
}

export function* getOpmAssignmentListSaga({ payload }) {
  const { fetchAllPages, onComplete, ...params } = payload;
  try {
    const response = yield call(
      fetchAllPages ? getAllOpmAssignmentsService : getOpmAssignmentListService,
      params,
    );
    yield put(setOpmAssignmentListAction(response));
  } catch (err) {
    console.error(err);
    GlobalModal.openOnce({
      title: 'Error',
      message: ErrorLevelConfig.SAGA.FETCH_ERROR,
      okText: 'Đóng',
      onOk: () => {},
    });
  } finally {
    if (!(yield cancelled()) && onComplete) {
      yield call(onComplete);
    }
  }
}

export function* getAgentsUnderAdSaga({ payload }) {
  try {
    yield put(loadingRequest());
    const response = yield call(getAgentsUnderAdService, payload);
    yield put(loadingSuccess());
    const agents = response?.data ?? response ?? [];
    yield put(setAgentsUnderAdAction(Array.isArray(agents) ? agents : []));
  } catch (err) {
    console.error(err);
    yield put(loadingSuccess());
    GlobalModal.openOnce({
      title: 'Error',
      message: ErrorLevelConfig.SAGA.FETCH_ERROR,
      okText: 'Đóng',
      onOk: () => {},
    });
  }
}

export function* getEligiblePoliciesSaga({ payload }) {
  try {
    yield put(loadingRequest());
    const response = yield call(getEligiblePoliciesService, payload);
    yield put(loadingSuccess());
    yield put(setEligiblePoliciesAction(response));
  } catch (err) {
    console.error(err);
    yield put(loadingSuccess());
    GlobalModal.openOnce({
      title: 'Error',
      message: ErrorLevelConfig.SAGA.FETCH_ERROR,
      okText: 'Đóng',
      onOk: () => {},
    });
  }
}

export function* getSuitableAgentsSaga({ payload }) {
  try {
    yield put(loadingRequest());
    const response = yield call(getSuitableAgentsService, payload);
    yield put(loadingSuccess());
    const agents = response?.data ?? response ?? [];
    yield put(setSuitableAgentsAction(Array.isArray(agents) ? agents : []));
  } catch (err) {
    console.error(err);
    yield put(loadingSuccess());
    GlobalModal.openOnce({
      title: 'Error',
      message: ErrorLevelConfig.SAGA.FETCH_ERROR,
      okText: 'Đóng',
      onOk: () => {},
    });
  }
}

export function* approveOpmAssignmentSaga({ payload }) {
  const {
    agentCode,
    policyNumbers,
    newAgentCode,
    onSuccess,
    decision = 'APPROVE',
  } = payload;
  try {
    if (agentCode) {
      yield put(loadingRequest());
      const validateResponse = yield call(validateAgentForAssignmentService, {
        agentCode,
        policyNumbers,
      });
      yield put(loadingSuccess());

      const validationData = validateResponse?.data ?? validateResponse ?? {};

      const isValid =
        validationData?.valid ??
        validateResponse?.valid ??
        validateResponse?.success === true;

      const errorCode =
        validationData?.errorCode ??
        validationData?.errors ??
        validateResponse?.errorCode ??
        validateResponse?.errors;

      if (!isValid && errorCode === 'OVER_OP_RECEIVED_IN_MONTH') {
        const agentValidationResult =
          validationData?.agentValidationResult ??
          validateResponse?.agentValidationResult;
        const opReceivedInMonth =
          agentValidationResult?.opReceivedInMonth ??
          validationData?.opReceivedInMonth ??
          '';
        const opReceivedInTransaction =
          agentValidationResult?.opReceivedInTransaction ??
          validationData?.opReceivedInTransaction ??
          '';
        GlobalModal.openOnce({
          title: 'Thông báo',
          message: `TVTC đã nhận ${opReceivedInMonth}/120 Hợp đồng trong tháng, bạn Anh/ Chị có chắc muốn tiếp tục chuyển giao ${opReceivedInTransaction} Hợp đồng?`,
          cancelText: 'Có',
          okText: 'Không',
          className: 'opm-confirm-modal',
          onCancel: () => {
            store.dispatch(loadingRequest());
            resumeStepFunctionService({
              policyNumbers,
              decision,
              newAgentCode,
            })
              .then((approveResponse) => {
                store.dispatch(loadingSuccess());
                const isSuccess =
                  approveResponse?.success === true ||
                  approveResponse?.statusCode === 200 ||
                  approveResponse?.status === 200;
                if (isSuccess) {
                  GlobalModal.openOnce({
                    title: 'Thành công',
                    message:
                      decision === 'CHANGE'
                        ? 'Cập nhật tư vấn mới thành công.'
                        : 'Phê duyệt hợp đồng chuyển giao thành công.',
                    okText: 'Đóng',
                    onOk: () => {
                      if (onSuccess) onSuccess();
                    },
                  });
                } else {
                  GlobalModal.openOnce({
                    title: 'Lỗi',
                    message:
                      approveResponse?.message ??
                      'Phê duyệt thất bại. Vui lòng thử lại.',
                    okText: 'Đóng',
                    onOk: () => {},
                  });
                }
              })
              .catch(() => {
                store.dispatch(loadingSuccess());
                GlobalModal.openOnce({
                  title: 'Error',
                  message: ErrorLevelConfig.SAGA.FETCH_ERROR,
                  okText: 'Đóng',
                  onOk: () => {},
                });
              });
          },
          onOk: () => {},
        });
        return;
      }

      if (!isValid && errorCode === 'OVER_TRANSFER_POLICY_RECEIVED') {
        const agentValidationResult =
          validationData?.agentValidationResult ??
          validateResponse?.agentValidationResult;
        const opReceivedInTotal =
          agentValidationResult?.opReceivedInTotal ??
          validationData?.opReceivedInTotal ??
          '';
        const opReceivedInTransaction =
          agentValidationResult?.opReceivedInTransaction ??
          validationData?.opReceivedInTransaction ??
          '';
        GlobalModal.openOnce({
          title: 'Thông báo',
          message: `TVTC đã nhận ${opReceivedInTotal}/1200 Hợp đồng, Anh/Chị có chắc muốn tiếp tục chuyển giao ${opReceivedInTransaction} Hợp đồng?`,
          cancelText: 'Có',
          okText: 'Không',
          className: 'opm-confirm-modal',
          onCancel: () => {
            store.dispatch(loadingRequest());
            resumeStepFunctionService({
              policyNumbers,
              decision,
              newAgentCode,
            })
              .then((approveResponse) => {
                store.dispatch(loadingSuccess());
                const isSuccess =
                  approveResponse?.success === true ||
                  approveResponse?.statusCode === 200 ||
                  approveResponse?.status === 200;
                if (isSuccess) {
                  GlobalModal.openOnce({
                    title: 'Thành công',
                    message:
                      decision === 'CHANGE'
                        ? 'Cập nhật tư vấn mới thành công.'
                        : 'Phê duyệt hợp đồng chuyển giao thành công.',
                    okText: 'Đóng',
                    onOk: () => {
                      if (onSuccess) onSuccess();
                    },
                  });
                } else {
                  GlobalModal.openOnce({
                    title: 'Lỗi',
                    message:
                      approveResponse?.message ??
                      'Phê duyệt thất bại. Vui lòng thử lại.',
                    okText: 'Đóng',
                    onOk: () => {},
                  });
                }
              })
              .catch(() => {
                store.dispatch(loadingSuccess());
                GlobalModal.openOnce({
                  title: 'Error',
                  message: ErrorLevelConfig.SAGA.FETCH_ERROR,
                  okText: 'Đóng',
                  onOk: () => {},
                });
              });
          },
          onOk: () => {},
        });
        return;
      }

      if (
        !isValid &&
        (errorCode === 'AGENT_NOT_ELIGIBLE' ||
          errorCode === 'AGENT_NOT_ACTIVE' ||
          errorCode === 'AGENT_COMPLIANCE_ISSUE' ||
          errorCode === 'MISSING_LICENSE' ||
          errorCode === 'AGENT_VALIDATION_FAILED')
      ) {
        GlobalModal.openOnce({
          title: 'Thông báo',
          message:
            'TVTC được chọn không thỏa điều kiện nhận hợp đồng chuyển giao',
          okText: 'Đóng',
          onOk: () => {},
        });
        return;
      }

      if (
        !isValid &&
        (errorCode === 'POLICY_ALREADY_ALLOCATED' ||
          errorCode === 'POLICY_ALREADY_ASSIGNED')
      ) {
        const alreadyAssignedPolicies =
          validationData?.alreadyAssignedPolicies ??
          validateResponse?.alreadyAssignedPolicies ??
          [];
        const policies = Array.isArray(alreadyAssignedPolicies)
          ? alreadyAssignedPolicies
          : [alreadyAssignedPolicies];
        const policyList = policies.join(', ');
        const prefix = policies.length > 1 ? 'Các hợp đồng' : 'Hợp đồng';
        GlobalModal.openOnce({
          title: 'Thông báo',
          message: `${prefix} ${policyList} đã được phân bổ`,
          okText: 'Đóng',
          onOk: () => {},
        });
        return;
      }

      if (!isValid) {
        const message = 'Tư vấn viên không hợp lệ để thực hiện phê duyệt.';
        GlobalModal.openOnce({
          title: 'Validate thất bại',
          message,
          okText: 'Đóng',
          onOk: () => {},
        });
        return;
      }
    }

    yield put(loadingRequest());
    const approveResponse = yield call(resumeStepFunctionService, {
      policyNumbers,
      decision,
      newAgentCode,
    });
    yield put(loadingSuccess());
    const isSuccess =
      approveResponse?.success === true ||
      approveResponse?.statusCode === 200 ||
      approveResponse?.status === 200;
    if (isSuccess) {
      GlobalModal.openOnce({
        title: 'Thành công',
        message:
          decision === 'CHANGE'
            ? 'Cập nhật tư vấn mới thành công.'
            : 'Phê duyệt hợp đồng chuyển giao thành công.',
        okText: 'Đóng',
        onOk: () => {
          if (onSuccess) onSuccess();
        },
      });
    } else {
      const approveResults = Array.isArray(approveResponse?.data?.results)
        ? approveResponse.data.results
        : [];
      const approveMessages = [];

      const agentValidationFailedResults = approveResults.filter(
        (r) => r?.errorCode === 'AGENT_VALIDATION_FAILED',
      );
      if (agentValidationFailedResults.length > 0) {
        const policies = agentValidationFailedResults
          .flatMap((r) =>
            Array.isArray(r?.policyNumbers)
              ? r.policyNumbers
              : [r?.policyNumbers],
          )
          .filter(Boolean);
        const policyList = policies.join(', ');
        approveMessages.push(
          `TVTC được chọn không thỏa điều kiện nhận hợp đồng chuyển giao, vui lòng đề xuất tư vấn mới cho HĐ: ${
            policyList ? ` ${policyList}` : ''
          }`,
        );
      }

      const alreadyAssignedResult = approveResults.find(
        (r) => r?.errorCode === 'POLICY_ALREADY_ASSIGNED',
      );
      if (alreadyAssignedResult) {
        const policies = Array.isArray(alreadyAssignedResult.policyNumbers)
          ? alreadyAssignedResult.policyNumbers
          : [alreadyAssignedResult.policyNumbers];
        const policyList = policies.join(', ');
        const prefix = policies.length > 1 ? 'Các hợp đồng' : 'Hợp đồng';
        approveMessages.push(`${prefix} ${policyList} đã được phân bổ`);
      }

      GlobalModal.openOnce({
        title: 'Thông báo',
        message:
          approveMessages.length > 0
            ? approveMessages.join(' ')
            : (approveResponse?.message ??
              'Phê duyệt thất bại. Vui lòng thử lại.'),
        okText: 'Đóng',
        onOk: () => {},
      });
    }
  } catch (err) {
    console.error(err);
    yield put(loadingSuccess());
    GlobalModal.openOnce({
      title: 'Error',
      message: ErrorLevelConfig.SAGA.FETCH_ERROR,
      okText: 'Đóng',
      onOk: () => {},
    });
  }
}

export function* rejectOpmAssignmentSaga({ payload }) {
  const { policyNumbers, onSuccess } = payload;
  try {
    yield put(loadingRequest());
    const response = yield call(resumeStepFunctionService, {
      policyNumbers,
      decision: 'REJECT',
    });
    yield put(loadingSuccess());
    const isSuccess =
      response?.success === true ||
      response?.statusCode === 200 ||
      response?.status === 200;
    if (isSuccess) {
      if (onSuccess) onSuccess();
      GlobalModal.openOnce({
        title: 'Thành công',
        message: 'Từ chối hợp đồng chuyển giao thành công.',
        okText: 'Đóng',
        onOk: () => {},
      });
    } else {
      GlobalModal.openOnce({
        title: 'Lỗi',
        message: response?.message ?? 'Từ chối thất bại. Vui lòng thử lại.',
        okText: 'Đóng',
        onOk: () => {},
      });
    }
  } catch (err) {
    console.error(err);
    yield put(loadingSuccess());
    GlobalModal.openOnce({
      title: 'Error',
      message: ErrorLevelConfig.SAGA.FETCH_ERROR,
      okText: 'Đóng',
      onOk: () => {},
    });
  }
}

export default function* opmSaga() {
  yield takeLatest(GET_AGENT_INFO_BY_CODE, getAgentInfoByCodeSaga);
  yield takeLatest(GET_OPM_ASSIGNMENT_LIST, getOpmAssignmentListSaga);
  yield takeLatest(GET_AGENTS_UNDER_AD, getAgentsUnderAdSaga);
  yield takeLatest(GET_ELIGIBLE_POLICIES, getEligiblePoliciesSaga);
  yield takeLatest(GET_SUITABLE_AGENTS, getSuitableAgentsSaga);
  yield takeLatest(APPROVE_OPM_ASSIGNMENT, approveOpmAssignmentSaga);
  yield takeLatest(REJECT_OPM_ASSIGNMENT, rejectOpmAssignmentSaga);
}
